import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UserSkinProfile } from '../../types';
import { useAppContext } from '../../context/AppContext';
import ApiService from '../../services/api';

interface Product {
  name: string;
  price: string;
  image_url: string;
  is_image_available: boolean;
  product_url: string;
  cat_sku_code: string;
  product_type: string;
  description: string;
}



interface RecommendationProps {
  profileData: UserSkinProfile[];
  clickedUserId?: string;
}

interface ClampedDescriptionProps {
  description: string;
  getTranslation: (key: string, defaultValue?: string) => string;
}

const ClampedDescription: React.FC<ClampedDescriptionProps> = ({ description, getTranslation }) => {
  const maxChars = 120;
  const isTruncated = description.length > maxChars;
  const visibleText = isTruncated ? description.slice(0, maxChars).trimEnd() : description;

  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: { xs: '0.7rem', sm: '0.8rem' },
        color: 'var(--color-text-secondary)',
        mb: { xs: 0, sm: 1 },
        lineHeight: 1.3,
      }}
    >
      {visibleText}
      <Typography
        component="span"
        variant="caption"
        sx={{
          display: isTruncated ? 'inline' : 'none',
          fontSize: { xs: '0.68rem', sm: '0.75rem' },
          color: 'var(--color-text-primary)',
          textDecoration: 'underline',
          fontWeight: 600,
          ml: 0.5,
        }}
      >
        {getTranslation("Txt814", "Read more")}
      </Typography>
    </Typography>
  );
};

const Recommendation: React.FC<RecommendationProps> = ({ profileData, clickedUserId }) => {
  const { 
    distributorId, 
    getTranslation, 
    distributorSessionsCache, 
    customerSessionsCache,
    distributorTableView,
    currentLanguage 
  } = useAppContext();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productTranslations, setProductTranslations] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Select the appropriate cache based on distributorTableView
  const sessionsCache = distributorTableView === 'customer' 
    ? customerSessionsCache 
    : distributorSessionsCache;

  // Fetch product translations
  useEffect(() => {
    const fetchProductTranslations = async () => {
      try {
        const api = new ApiService();
        const language = currentLanguage || 'en';
        const response = await api.getProductTranslations(language);
        if (response.data && response.data.product_translations) {
          setProductTranslations(response.data.product_translations);
        }
      } catch (err) {
        console.error('[Recommendation] Error fetching product translations:', err);
        // Don't set error here, just log it - translations are optional
      }
    };

    if (currentLanguage) {
      fetchProductTranslations();
    }
  }, [currentLanguage]);

  // Extract products from cached session data
  useEffect(() => {
    const extractProducts = async () => {
      if (!distributorId) {
        setError('No distributor ID available');
        setLoading(false);
        return;
      }

      // Check if we have cached data
      if (!sessionsCache.data || sessionsCache.isLoading) {
        setLoading(true);
        return;
      }

      if (sessionsCache.error) {
        setError(sessionsCache.error);
        setLoading(false);
        return;
      }

      try {
        const sessions = sessionsCache.data;
        
        if (sessions && sessions.length > 0) {
          // Find the current session based on clickedUserId or profileData
          const targetSessionId = clickedUserId || profileData[0]?._id;
          
          const currentSession = sessions.find(session => 
            session._id === targetSessionId
          );
          
          if (currentSession && (currentSession as any).recommendations) {
            // Extract all products from recommendations
            const allProducts: Product[] = [];
            
            (currentSession as any).recommendations.forEach((rec: any) => {
              if (rec.products) allProducts.push(...rec.products);
              if (rec.must_have) allProducts.push(...rec.must_have);
              if (rec.am_products) allProducts.push(...rec.am_products);
              if (rec.pm_products) allProducts.push(...rec.pm_products);
              if (rec.weekly_products) allProducts.push(...rec.weekly_products);
            });

            // Remove duplicates based on cat_sku_code
            const uniqueProducts = allProducts.filter((product, index, self) => 
              index === self.findIndex(p => p.cat_sku_code === product.cat_sku_code)
            );
 
            setProducts(uniqueProducts);
          } else {
            setProducts([]);
          }
        } else {
          setProducts([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error extracting products:', err);
        setError('Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    extractProducts();
  }, [distributorId, customerSessionsCache, distributorSessionsCache, clickedUserId, profileData[0]?._id, distributorTableView]);

  // Helper function to find translation object by cat_sku_code
  const findProductTranslation = (catSkuCode: string) => {
    if (!productTranslations || typeof productTranslations !== "object") {
      return null;
    }
    return productTranslations[catSkuCode] || null;
  };

  // Helper function to get translated product name
  const getTranslatedProductName = (product: Product): string => {
    if (!product.cat_sku_code) {
      return product.name;
    }
    
    const translation = findProductTranslation(product.cat_sku_code);
    if (translation && translation.name) {
      // If it's a translation key (TxtXXX), use getTranslation, otherwise use as-is
      if (/^Txt\d+$/i.test(translation.name)) {
        return getTranslation(translation.name, product.name);
      }
      return translation.name;
    }
    
    return product.name;
  };

  // Helper function to get translated product description
  const getTranslatedProductDescription = (product: Product): string => {
    if (!product.cat_sku_code) {
      return product.description || "";
    }
    
    const translation = findProductTranslation(product.cat_sku_code);
    if (translation && translation.description) {
      // If it's a translation key (TxtXXX), use getTranslation, otherwise use as-is
      if (/^Txt\d+$/i.test(translation.description)) {
        return getTranslation(translation.description, product.description || "");
      }
      return translation.description;
    }
    
    return product.description || "";
  };

  // Helper function to get translated product image URL
  const getTranslatedProductImageUrl = (product: Product): string => {
    if (!product.cat_sku_code) {
      return product.image_url;
    }
    
    const translation = findProductTranslation(product.cat_sku_code);
    if (translation && translation.image) {
      // Use the image from translation (it's already a URL, not a translation key)
      return translation.image;
    }
    
    return product.image_url;
  };

  const resolveDescription = (description?: string): string => {
    const trimmed = (description || "").trim();
    if (!trimmed) return "";

    if (/^Txt\d+$/i.test(trimmed)) {
      const translated = getTranslation(trimmed, trimmed);
      return translated;
    }

    return trimmed;
  };

  const handleProductCardClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleCloseProductDialog = () => {
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
   <CircularProgress size={20} sx={{ color: "var(--color-text-inverse)" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No products available at the moment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Complete your skin analysis to get personalized product recommendations
        </Typography>
      </Box>
    );
  }



  const renderProductItem = (product: Product, index: number) => (
    <Card
      key={`${product.cat_sku_code}-${index}`}
      onClick={() => handleProductCardClick(product)}
      sx={{
        height: 'auto',
        width: '100%',
        maxWidth: { xs: '100%', sm: '350px' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        },
        cursor: 'pointer',
      }}
    >
      <CardMedia
        component="img"
        height="220"
        image={product.is_image_available ? getTranslatedProductImageUrl(product) : '/img-not-found.svg'}
        alt={getTranslatedProductName(product)}
        sx={{
          objectFit: 'contain',
          backgroundColor: 'var(--color-background)',
          width: '100%',
          flexShrink: 0,
          height: { xs: '120px', sm: '200px', md: '220px' },
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;

          // Try alternative formats if PNG fails
          if (product.image_url && product.image_url.endsWith('.png')) {
            const jpegUrl = product.image_url.replace('.png', '.jpeg');

            target.src = jpegUrl;
          } else {
            target.src = '/img-not-found.svg';
          }
        }}
      />
      
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        p: { xs: 1, sm: 2, md: 2.5 },
        justifyContent: 'space-between',
        minHeight: 0,
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontSize: { xs: '0.8rem', sm: '1rem' },
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              mb: { xs: 0.5, sm: 1.5 },
              lineHeight: 'normal',
              minHeight: { xs: 'auto', sm: '2.6rem' },
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getTranslatedProductName(product)}
          </Typography>

          {/* <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-secondary)',
              fontSize: '0.875rem',
              mb: 1,
            }}
          >
            {product.product_type}
          </Typography> */}
          {(() => {
            const translatedDescription = getTranslatedProductDescription(product);
            const description = resolveDescription(translatedDescription);
            if (!description || /^Txt\d+$/i.test(description)) {
              return null;
            }
            return (
              <ClampedDescription
                description={description}
                getTranslation={getTranslation}
              />
            );
          })()}
         
        </Box>
      </CardContent>
    </Card>
  );

  
  let renderCount = 0;

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 3 }, 
      textAlign: 'left' 
    }}>
      <Typography sx={{
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
                fontWeight: 700,
                lineHeight: { xs: '20px', sm: '22px' },
                color: 'var(--color-text-primary)',
                paddingBottom: { xs: "15px", sm: "20px" }
            }}>
              {getTranslation("Txt617")}
            </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="flex-start">
        {products.map((product: Product, index: number) => {
          renderCount++;
          return (
            <Grid 
              item 
              xs={6} 
              sm={6} 
              md={4} 
              lg={4} 
              key={`${product.cat_sku_code}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              {renderProductItem(product, index)}
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={Boolean(selectedProduct)}
        onClose={handleCloseProductDialog}
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '35%' },
            maxWidth: { xs: '90%', sm: '35%' },
            maxHeight: { xs: 'auto', sm: 'auto' },
            minHeight: { xs: 'auto', sm: 'auto' },
            minWidth: { xs: '90%', sm: 'auto' },
            mx: 'auto',
          },
        }}
      >
        {selectedProduct && (
          <>
              <IconButton
                       aria-label="close"
                       onClick={handleCloseProductDialog}
                       sx={{
                         position: 'absolute',
                         right: 8,
                         top: 8,
                         color: 'var(--color-text-secondary)',
                       }}
                     >
                <CloseIcon />
              </IconButton>
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  component="img"
                  src={
                    selectedProduct.is_image_available
                      ? getTranslatedProductImageUrl(selectedProduct)
                      : '/img-not-found.svg'
                  }
                  alt={getTranslatedProductName(selectedProduct)}
                  sx={{
                    width: '100%',
                    maxHeight: 220,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const target = e.currentTarget;
                    target.src = '/img-not-found.svg';
                  }}
                />
                <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '16px', sm: '1.5rem', md: '24px' }, paddingY: '0', fontWeight: 500, lineHeight: { xs: '20px', sm: '22px' }, color: 'var(--color-text-primary)' }}>
                     {getTranslatedProductName(selectedProduct)}
            </DialogTitle>

                {selectedProduct.product_type && (
                  <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}>
                    {selectedProduct.product_type}
                  </Typography>
                )}

                {selectedProduct.price && (
                  <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {selectedProduct.price}
                  </Typography>
                )}

                {(() => {
                  const translatedDescription = getTranslatedProductDescription(selectedProduct);
                  const description = resolveDescription(translatedDescription);
                  if (!description || /^Txt\d+$/i.test(description)) return null;
                  return (
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', textAlign: 'center' }}>
                      {description}
                    </Typography>
                  );
                })()}

                {selectedProduct.product_url && (
                  <Button
                    variant="contained"
                    onClick={() => window.open(selectedProduct.product_url, '_blank', 'noopener,noreferrer')}
                    sx={{ alignSelf: 'center', mt: 1 }}
                  >
                    {getTranslation('Txt151', 'View Product')}
                  </Button>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Recommendation;
